import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderText, SectionContainer, SectionSubtitle } from '../../../components/Modals';
import { Flex } from '../../../components/ui/Flex';
import { useWatch } from 'react-hook-form';
import ToggleInput from '../../../components/ui/ToggleInput';
import { useSelector } from 'react-redux';
import { VOPAY_ACCOUNT_STATUS } from '../../../helper/constants';
import { normalizeVopayAccountStatus } from '../../payments/PaymentUtils';
import AkuvoxConfigInput from './AkuvoxConfigInput';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../components/ui/Form';
import { Select, SelectItem } from '../../../components/ui/Select';
import { SettingsModalInput } from '../../settings/SettingsModals';
import { isCobaltAmenityBookingBuilding } from '../constants';
import { SMART_LOCKER_PROVIDERS } from '../../../utils/mappings';
import ImageUploadWithPreviewV3 from '../../../components/ImageUploadWithPreviewV3';
import { isYaleLockProvider } from '../../../utils/lockInfo';

const FeatureModuleFormInputs = ({
  form,
  type,
  isEditBuilding,
  buildingId,
  hasExistingVopayAccount = false,
  existingHomepageImageUrl = ''
}) => {
  const { t } = useTranslation();
  const addPayment = useSelector((state) => state.addPayment);
  const enableVopay = useWatch({
    control: form.control,
    name: 'enable_vopay'
  });
  const enableV2HomePage = useWatch({
    control: form.control,
    name: 'enable_v2_home_page'
  });
  const prevEnableV2HomePage = useRef(enableV2HomePage);
  const lockProvider = useWatch({
    control: form.control,
    name: 'lock_provider'
  });
  // Generic flag on the API; only offered for Yale buildings for now.
  const displayLowConnectivityBannerToggle = type === 'building' && isYaleLockProvider(lockProvider);

  useEffect(() => {
    if (type !== 'building') return;
    if (enableV2HomePage === prevEnableV2HomePage.current) return;

    prevEnableV2HomePage.current = enableV2HomePage;
    form.trigger('homepage_image');
  }, [enableV2HomePage, form, type]);

  const isOrgLevel = type === 'org';
  const vopayAccountStatus = addPayment.organizationOnboardingData?.status;

  const isVopayAvailable = () => {
    if (isOrgLevel) return true;

    const approvedStatuses = [VOPAY_ACCOUNT_STATUS.APPROVED, VOPAY_ACCOUNT_STATUS.ACTIVE];
    return type === 'building' && approvedStatuses.includes(normalizeVopayAccountStatus(vopayAccountStatus));
  };

  const buildingEmail = useWatch({
    control: form.control,
    name: 'building_email'
  });

  const shouldShowVopayEmailInput =
    type === 'building' && enableVopay && (!isEditBuilding || !hasExistingVopayAccount);

  const shouldShowVopayEmailReadOnly =
    type === 'building' && enableVopay && isEditBuilding && hasExistingVopayAccount && buildingEmail;

  const showAutomaticBookingAccess =
    type === 'building' && isEditBuilding && isCobaltAmenityBookingBuilding(buildingId);

  return (
    <SectionContainer>
      <HeaderText alt>{t('Feature Modules')}</HeaderText>
      <SectionSubtitle>
        {t('Enable device-level integrations based on what’s installed.')}
      </SectionSubtitle>
      <Flex direction="column" gap="16px">
        {showAutomaticBookingAccess && (
          <ToggleInput
            form={form}
            name="enable_automatic_booking_access"
            label={t('Enable Automatic Booking Access')}
            description={t('Automatically grant and revoke door access for amenity bookings')}
          />
        )}

        <ToggleInput
          form={form}
          name="enable_thermostats"
          label={t('Enable Thermostats')}
          description={t('Allow smart thermostat control')}
        />

        <ToggleInput
          form={form}
          name="enable_leak_detectors"
          label={t('Enable Leak Detectors')}
          description={t('Enable alerts for water leaks')}
        />

        <ToggleInput
          form={form}
          name="enable_deliveries"
          label={t('Enable Deliveries')}
          description={t('Enable delivery management features')}
        />

        {type === 'building' && isEditBuilding && (
          <>
            <FormField
              control={form.control}
              name="smart_locker_provider"
              render={({ field }) => (
                <FormItem alt>
                  <FormLabel fontSize="14px">{t('Smart Locker Provider')}</FormLabel>
                  <FormControl>
                    <Select placeholder={t('Select smart locker provider')} {...field}>
                      {SMART_LOCKER_PROVIDERS.map((item) => (
                        <SelectItem value={item.id} key={item.id}>
                          {item.value}
                        </SelectItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}


        <AkuvoxConfigInput showIntercomDisplaySettings={!(type === 'building' && isEditBuilding)} />

        <ToggleInput
          form={form}
          name="enable_vopay"
          label={t('Enable vopay integration')}
          description={
            isVopayAvailable()
              ? t('Activates the vopay payment system integration')
              : t('Vopay is not yet set up for this organization')
          }
          disabled={!isVopayAvailable()}
        />

        {(shouldShowVopayEmailInput || shouldShowVopayEmailReadOnly) && (
          <FormField
            control={form.control}
            name="building_email"
            render={({ field }) => (
              <FormItem
                alt
                cssOverride={`
                  border: 1px solid #d8dbe0;
                  border-radius: 12px;
                  padding: 12px;
                `}
              >
                <FormLabel v3>{t('Email address')}</FormLabel>
                <FormControl row>
                  <SettingsModalInput
                    type="text"
                    placeholder={t('Enter email address')}
                    readOnly={shouldShowVopayEmailReadOnly}
                    disabled={shouldShowVopayEmailReadOnly}
                    {...field}
                  />
                </FormControl>
                {!shouldShowVopayEmailReadOnly && <FormMessage />}
              </FormItem>
            )}
          />
        )}

        <ToggleInput
          form={form}
          name="enable_security_cameras"
          label={t('Enable Security Cameras')}
          description={t('Provide access to live camera feeds')}
        />

        {type === 'building' && (
          <ToggleInput
            form={form}
            name="enable_ai_nvr"
            label={t('Enable AI NVR')}
            description={t('Show the AI NVR page for this building')}
          />
        )}

        <ToggleInput
          form={form}
          name="suite_enabled"
          label={t('Enable Multi Lock')}
          description={t(
            'Enable "student housing" mode with smart locks on both shared units and private rooms'
          )}
        />

        <ToggleInput
          form={form}
          name="enable_license_plates"
          label={t('Enable Parking')}
          description={t('Enable parking management tools')}
        />

        <ToggleInput
          form={form}
          name="enable_chargers"
          label={t('Enable EV Chargers')}
          description={t('Enable URL redirect to Jplug EV Charger web flow.')}
        />

        <ToggleInput
          form={form}
          name="enable_document_storage"
          label={t('Enable Document Storage')}
          description={t('Allow document uploads and management for residents and admins')}
        />

        <ToggleInput
          form={form}
          name="enable_events"
          label={t('Enable Events')}
          description={t('Allow event creation and management for the building')}
        />

        <ToggleInput
          form={form}
          name="enable_v2_home_page"
          label={t('Enable V2 Home Page')}
          description={t('Enable the V2 home page experience for residents')}
        />

        {type === 'building' && enableV2HomePage && (
          <ImageUploadWithPreviewV3
            form={form}
            existingImageUrl={existingHomepageImageUrl}
            fieldName="homepage_image"
            instructionText={t('Drag and drop your V2 home page image here.')}
            altText={t('V2 home page image')}
            deleteButtonText={t('Delete Image')}
          />
        )}

        {displayLowConnectivityBannerToggle && (
          <ToggleInput
            form={form}
            name="display_low_connectivity_banner"
            label={t('Display Low Connectivity Banner')}
            description={t(
              'Show residents a banner in the mobile app to open the app where they have internet before entering areas with no or low connectivity'
            )}
          />
        )}
      </Flex>
    </SectionContainer>
  );
};

export default FeatureModuleFormInputs;
